// Known yard vehicles — Reference tab (Suresh 2026-10-06).
//
// Carriers (v2, 2026-10-07): cabs swap trailers, so instead of
// trailer->cab links each carrier card shows its CAB plates (straight
// from SoilFlo loads) and its TRAILERS (learned nightly from the camera,
// or confirmed by a person). Yard visits matches a trailer to its
// carrier's cab read at the same moment, else that carrier's nearest load.
// Other known vehicles: staff cars, delivery vans, cleanup trucks,
// company names painted on trucks, text the camera misreads — each
// with a default reason so Yard visits never asks about them again.
// Data: /api/yard/vehicles.
import { useEffect, useState } from 'react';

const ROLE_META = {
  staff:       { icon: '👷', label: 'Staff vehicles',          owner: 'Person' },
  delivery:    { icon: '📦', label: 'Delivery / suppliers',    owner: 'Company' },
  visitor:     { icon: '🙋', label: 'Regular visitors',        owner: 'Person / company' },
  cleanup:     { icon: '🧹', label: 'Site-cleanup trucks',     owner: 'Company' },
  customer:    { icon: '🏷', label: 'Company name on truck',   owner: 'Customer' },
  not_vehicle: { icon: '🔤', label: 'Text, not a vehicle',     owner: '' }
};

export default function KnownVehicles({ canWrite = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [filter, setFilter] = useState('');
  const [add, setAdd] = useState({ plate: '', role: 'staff', owner: '', label: '' });

  async function load() {
    setErr(null);
    try {
      const r = await fetch('/api/yard/vehicles', { credentials: 'include' });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.error || ('HTTP ' + r.status));
      setData(j);
    } catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  async function call(method, body, qs = '') {
    const r = await fetch('/api/yard/vehicles' + qs, {
      method, credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j.ok) throw new Error(j.error || ('HTTP ' + r.status));
    return j;
  }
  const act = (fn) => async (...a) => { try { await fn(...a); load(); } catch (e) { alert(e.message); } };
  const carrierNames = data ? data.carriers.map(c => c.carrier) : [];
  const confirmTrailer = act((plate, carrier) => call('POST', { action: 'confirm-trailer', plate, carrier }));
  const moveTrailer = act(async (plate) => {
    const c = window.prompt('Which carrier does trailer ' + plate + ' belong to?\n(' + carrierNames.join(', ') + ')', '');
    if (c && c.trim()) await call('POST', { action: 'confirm-trailer', plate, carrier: c.trim() });
  });
  const addTrailer = act(async (carrier) => {
    const p = window.prompt('Trailer plate for ' + carrier + ' (as the camera reads it):', '');
    if (p && p.trim()) await call('POST', { action: 'confirm-trailer', plate: p.trim(), carrier });
  });
  const removeVehicle = act((v) => window.confirm('Remove ' + v.plate + ' from known vehicles?') && call('DELETE', null, '?plate=' + encodeURIComponent(v.plate)));
  const addVehicle = act(async () => {
    await call('PUT', add);
    setAdd({ plate: '', role: add.role, owner: '', label: '' });
  });
  async function relearn() {
    setMsg('Re-learning trailer → carrier from the last 92 days…');
    try { const j = await call('POST', null, '?relearn=1'); setMsg('Learned ' + j.trailersLearned + ' trailer(s) → carrier.'); load(); }
    catch (e) { setMsg('Failed: ' + e.message); }
  }

  const f = filter.trim().toUpperCase().replace(/[^A-Z0-9 ]/g, '');
  const match = (...xs) => !f || xs.some(x => String(x || '').toUpperCase().includes(f));
  const carriers = data ? data.carriers.filter(c => match(c.carrier, ...c.cabs.map(x => x.plate), ...c.trailers.map(t => t.plate))) : [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, margin: '0.5rem 0' }}>
        <div className="small" style={{ color: '#555', maxWidth: 720 }}>
          Vehicles the gate camera sees regularly, and what they're there for. Yard visits uses this list to assign reasons automatically.
          Each carrier lists its cab plates (from SoilFlo) and its trailers (📒 learned, ✅ confirmed). Cabs swap trailers, so a trailer is matched to its carrier's truck by timing.
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input placeholder="Search plate / carrier…" value={filter} onChange={e => setFilter(e.target.value)} style={{ width: 180 }} />
          {canWrite && <button className="refresh-btn small" onClick={relearn}>Re-learn trailers</button>}
        </div>
      </div>
      {msg && <div className="small" style={{ marginBottom: '0.4rem' }}>{msg}</div>}
      {err && <div className="error small">{err}</div>}

      {data && (
        <>
          <h3 style={{ marginBottom: '0.3rem' }}>🚛 Carriers — cabs &amp; trailers <span className="small" style={{ fontWeight: 400, color: '#666' }}>({carriers.length})</span></h3>
          {carriers.length === 0 && <div className="small">No carriers yet — they appear once outgoing loads are imported.</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: '0.5rem' }}>
            {carriers.map(c => (
              <div key={c.carrier} className="chart-card" style={{ padding: '0.5rem 0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>{c.carrier}</strong>
                  {canWrite && <button className="link small" onClick={() => addTrailer(c.carrier)}>+ trailer</button>}
                </div>
                <div className="small" style={{ color: '#666', margin: '0.3rem 0 0.15rem' }}>Cab plates (SoilFlo, last 92 days)</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 10px' }}>
                  {c.cabs.length === 0 && <span className="small" style={{ color: '#999' }}>none with a plate (unit # only)</span>}
                  {c.cabs.map(x => (
                    <span key={x.plate} className="small" title={x.loads + ' load(s), last ' + (x.last || '?')}>
                      <code>{x.shown}</code> <span style={{ color: '#888' }}>{x.loads}</span>
                    </span>
                  ))}
                </div>
                <div className="small" style={{ color: '#666', margin: '0.4rem 0 0.15rem' }}>Trailers</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 12px' }}>
                  {c.trailers.length === 0 && <span className="small" style={{ color: '#999' }}>none yet</span>}
                  {c.trailers.map(t => (
                    <span key={t.plate} className="small" style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}
                          title={(t.learned ? 'Learned from the camera' : 'Confirmed') + ' · seen ' + t.days + ' day(s)' + (t.last ? ', last ' + t.last : '')}>
                      {t.learned ? '📒' : '✅'} <code>{t.plate}</code> <span style={{ color: '#888' }}>{t.days}d</span>
                      {canWrite && t.learned && <button className="link small" onClick={() => confirmTrailer(t.plate, c.carrier)}>confirm</button>}
                      {canWrite && <button className="link small" style={{ color: '#666' }} onClick={() => moveTrailer(t.plate)}>move</button>}
                      {canWrite && <button className="link small" style={{ color: '#999' }} onClick={() => removeVehicle({ plate: t.plate })}>✕</button>}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {data.unassignedTrailers && data.unassignedTrailers.length > 0 && (
            <div className="small" style={{ marginTop: '0.4rem' }}>
              Trailers with no carrier: {data.unassignedTrailers.map(t => (
                <span key={t.plate} style={{ marginRight: 10 }}><code>{t.plate}</code>{canWrite && <button className="link small" onClick={() => moveTrailer(t.plate)}>assign</button>}</span>
              ))}
            </div>
          )}

          <h3 style={{ margin: '1rem 0 0.3rem' }}>Other known vehicles</h3>
          {canWrite && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.5rem' }}>
              <input placeholder="Plate / text as camera reads it" value={add.plate} onChange={e => setAdd({ ...add, plate: e.target.value })} style={{ width: 200 }} />
              <select value={add.role} onChange={e => setAdd({ ...add, role: e.target.value })}>
                {Object.entries(ROLE_META).map(([r, m]) => <option key={r} value={r}>{m.icon} {m.label}</option>)}
                <option value="trailer">🚛 Trailer</option>
              </select>
              <input placeholder={add.role === 'customer' ? 'Customer (required)' : add.role === 'trailer' ? 'Carrier' : 'Owner / company'} value={add.owner} onChange={e => setAdd({ ...add, owner: e.target.value })} style={{ width: 180 }} />
              <input placeholder="Note (optional)" value={add.label} onChange={e => setAdd({ ...add, label: e.target.value })} style={{ width: 160 }} />
              <button className="refresh-btn small" disabled={!add.plate.trim()} onClick={addVehicle}>Add</button>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '0.5rem' }}>
            {Object.entries(ROLE_META).map(([role, m]) => {
              const vs = data.vehicles.filter(v => v.role === role && match(v.plate, v.owner, v.label));
              return (
                <div key={role} className="chart-card" style={{ padding: '0.5rem 0.75rem' }}>
                  <div style={{ fontWeight: 700 }}>{m.icon} {m.label} <span className="small" style={{ fontWeight: 400, color: '#666' }}>({vs.length})</span></div>
                  {vs.length === 0
                    ? <div className="small" style={{ color: '#999' }}>None yet — tick "always" on Yard visits to add.</div>
                    : <table className="fs-table tidy" style={{ fontSize: '0.85em' }}>
                        <tbody>{vs.map(v => (
                          <tr key={v.plate}>
                            <td><code>{v.plate}</code></td>
                            <td>{v.owner || ''}{v.label ? <span className="small" style={{ color: '#888' }}> · {v.label}</span> : ''}</td>
                            {canWrite && <td style={{ textAlign: 'right' }}><button className="link small" style={{ color: '#999' }} onClick={() => removeVehicle(v)}>remove</button></td>}
                          </tr>
                        ))}</tbody>
                      </table>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
